import { useState, useEffect, useRef } from "react";
import QRCode from "react-qr-code";
import copy from "clipboard-copy";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Mask } from "@/utils/mask";
import { checkPaymentAdvanceStatus } from "@/Api/paymentAdvances";
import { checkPaymentStatus } from "@/Api/sales";
import { PAYMENT_ADVANCE_STATUS, PIX_STATUS } from "@/utils/constants";

interface CreatedPaymentAdvance {
  id: number;
  uuid: string;
  amount: number;
  pix_qrcode: string;
  pix_qrcode_image?: string;
  pix_location?: string;
  pix_txid?: string;
  status: string;
  sale_id?: number; // ID da venda (para transações de venda)
  isTransaction?: boolean; // Indica se é SaleTransaction (novo formato)
}

interface PreSalePixDialogProps {
  isOpen: boolean;
  onClose: () => void;
  paymentAdvances: CreatedPaymentAdvance[];
}

export function PreSalePixDialog({
  isOpen,
  onClose,
  paymentAdvances,
}: PreSalePixDialogProps) {
  const [copied, setCopied] = useState(false);
  const [checking, setChecking] = useState(false);
  const [paymentStatuses, setPaymentStatuses] = useState<Record<string, string>>({});
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  // Inicializa os status
  useEffect(() => {
    const initialStatuses: Record<string, string> = {};
    paymentAdvances.forEach((pa) => {
      initialStatuses[pa.uuid] = pa.status;
    });
    setPaymentStatuses(initialStatuses);
  }, [paymentAdvances]);

  const handleCopyPix = async (pixCode: string) => {
    try {
      await copy(pixCode);
      setCopied(true);
      toast.success("Codigo PIX copiado!");
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast.error("Erro ao copiar codigo PIX");
    }
  };

  const checkAllStatuses = async () => {
    try {
      setChecking(true);

      for (const pa of paymentAdvances) {
        const currentStatus = paymentStatuses[pa.uuid];

        // Se já está pago, pula
        if (currentStatus === PAYMENT_ADVANCE_STATUS.PAID || currentStatus === PIX_STATUS.PAID) continue;

        // Se é uma transação de venda (novo formato), verifica pelo sale_id
        if (pa.isTransaction && pa.sale_id) {
          const { data, ok } = await checkPaymentStatus(pa.sale_id.toString());
          if (!ok) continue;

          // Verifica se a transação específica foi paga
          const transactions = data?.response?.transactions || [];
          const thisTransaction = transactions.find((t: any) => t.id === pa.id);

          if (thisTransaction?.status === PIX_STATUS.PAID) {
            setPaymentStatuses((prev) => ({
              ...prev,
              [pa.uuid]: PIX_STATUS.PAID,
            }));
            toast.success("Pagamento confirmado!");
          }
        } else {
          // Formato antigo (PaymentAdvance)
          const { data, ok } = await checkPaymentAdvanceStatus(pa.uuid);
          if (!ok) continue;

          const status = data?.response?.status;
          if (status) {
            setPaymentStatuses((prev) => ({
              ...prev,
              [pa.uuid]: status,
            }));

            if (status === PAYMENT_ADVANCE_STATUS.PAID) {
              toast.success("Pagamento confirmado!");
            }
          }
        }
      }
    } catch (error) {
      console.error("Erro ao verificar status do pagamento:", error);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (isOpen && paymentAdvances.length > 0) {
      checkAllStatuses();

      const id = setInterval(checkAllStatuses, 5000);
      intervalIdRef.current = id;

      return () => {
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }
      };
    }
  }, [isOpen, paymentAdvances]);

  const [selectedIndex, setSelectedIndex] = useState(0);

  if (paymentAdvances.length === 0) {
    return null;
  }

  const hasMultiple = paymentAdvances.length > 1;
  const currentPayment = paymentAdvances[selectedIndex];
  const totalAmount = paymentAdvances.reduce((sum, pa) => sum + Number(pa.amount), 0);

  const isCurrentPaid = paymentStatuses[currentPayment?.uuid] === PAYMENT_ADVANCE_STATUS.PAID ||
                        paymentStatuses[currentPayment?.uuid] === PIX_STATUS.PAID;

  const allPaid = paymentAdvances.every((pa) =>
    paymentStatuses[pa.uuid] === PAYMENT_ADVANCE_STATUS.PAID ||
    paymentStatuses[pa.uuid] === PIX_STATUS.PAID
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {allPaid ? "Pagamentos Confirmados" : `Pagamento PIX${hasMultiple ? ` (${selectedIndex + 1}/${paymentAdvances.length})` : ""}`}
          </DialogTitle>
          <DialogDescription>
            {allPaid
              ? "Todos os pagamentos foram confirmados com sucesso."
              : "Escaneie o QR Code ou copie o codigo PIX para realizar o pagamento."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center p-4">
          {allPaid ? (
            <div className="flex flex-col items-center justify-center py-6">
              <div className="bg-green-100 p-4 rounded-full mb-4">
                <CheckCircle2 className="w-16 h-16 text-green-600" />
              </div>
              <h4 className="text-xl font-bold text-green-600 mb-2">
                {hasMultiple ? "Todos os Pagamentos Confirmados!" : "Pagamento Confirmado!"}
              </h4>
              <p className="text-center text-gray-600 mb-4">
                O pagamento de {Mask.formatCurrency(totalAmount)} foi confirmado
                com sucesso.
              </p>
              <Button variant="outline" className="mt-4" onClick={onClose}>
                Fechar
              </Button>
            </div>
          ) : (
            <>
              {/* Seletor de PIX quando há múltiplos */}
              {hasMultiple && (
                <div className="w-full mb-4">
                  <p className="text-sm text-center text-muted-foreground mb-2">
                    Selecione o PIX para pagar:
                  </p>
                  <div className="flex gap-2 justify-center flex-wrap">
                    {paymentAdvances.map((pa, index) => {
                      const isPaid = paymentStatuses[pa.uuid] === PAYMENT_ADVANCE_STATUS.PAID ||
                                     paymentStatuses[pa.uuid] === PIX_STATUS.PAID;
                      return (
                        <Button
                          key={pa.uuid}
                          variant={selectedIndex === index ? "default" : "outline"}
                          size="sm"
                          onClick={() => setSelectedIndex(index)}
                          className={isPaid ? "bg-green-100 border-green-300 text-green-700 hover:bg-green-200" : ""}
                        >
                          {isPaid && <CheckCircle2 className="w-3 h-3 mr-1" />}
                          PIX {index + 1} - {Mask.formatCurrency(Number(pa.amount))}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}

              {isCurrentPaid ? (
                <div className="flex flex-col items-center justify-center py-4">
                  <div className="bg-green-100 p-3 rounded-full mb-3">
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  </div>
                  <h4 className="text-lg font-bold text-green-600 mb-2">
                    PIX {selectedIndex + 1} Pago!
                  </h4>
                  <p className="text-center text-gray-600 text-sm">
                    {Mask.formatCurrency(Number(currentPayment.amount))}
                  </p>
                </div>
              ) : (
                <>
                  <div className="bg-white p-4 rounded-lg mb-4 border">
                    <QRCode
                      value={currentPayment.pix_qrcode || ""}
                      size={192}
                      style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                    />
                  </div>

                  <div className="w-full max-w-xs mb-4">
                    <p className="text-sm text-center mb-2 text-muted-foreground">
                      Valor a pagar:
                    </p>
                    <p className="text-2xl font-bold text-center text-green-600 mb-4">
                      {Mask.formatCurrency(Number(currentPayment.amount))}
                    </p>

                    <div className="flex items-center gap-2 p-2 bg-gray-100 dark:bg-zinc-700 rounded-md">
                      <p className="text-xs flex-1 truncate font-mono">
                        {currentPayment.pix_qrcode}
                      </p>
                      <Button
                        size="icon"
                        onClick={() => handleCopyPix(currentPayment.pix_qrcode)}
                        className="h-8 w-8 shrink-0"
                      >
                        {copied ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              )}

              <div className="w-full max-w-xs space-y-2">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={checkAllStatuses}
                  disabled={checking}
                >
                  {checking ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verificando...
                    </>
                  ) : (
                    "Verificar pagamento"
                  )}
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  O status do pagamento e verificado automaticamente a cada 5
                  segundos.
                </p>

                <Button
                  variant="ghost"
                  className="w-full text-muted-foreground"
                  onClick={onClose}
                >
                  Fechar e aguardar pagamento
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
